import React, { useState, KeyboardEvent, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { logDebug } from '@/utils/errorHandler';

interface TagInputProps {
  id: string;
  tags: string[];
  setTags: (tags: string[]) => void;
  placeholder?: string;
  maxTags?: number;
  className?: string;
  disabled?: boolean;
}

export function TagInput({
  id,
  tags,
  setTags,
  placeholder: _placeholder = 'Add tag...',
  maxTags = 5,
  className,
  disabled = false,
}: TagInputProps) {
  const [inputValue, setInputValue] = useState('');

  // Synchronize with parent when tags change externally
  useEffect(() => {
    logDebug('TagInput: tags prop updated', { tags });
  }, [tags]);

  const addTag = (tag: string) => {
    const trimmedTag = tag.trim();

    // Validate tag
    if (!trimmedTag || trimmedTag.length < 1) return;
    if (tags.includes(trimmedTag)) return;
    if (tags.length >= maxTags) return;

    logDebug('Adding tag', {
      tag: trimmedTag,
      currentTags: tags,
      newTagsLength: tags.length + 1,
    });
    const newTags = [...tags, trimmedTag];
    logDebug('New tags array created', { newTags });
    setTags(newTags);
    setInputValue('');
  };

  const removeTag = (indexToRemove: number) => {
    logDebug('Removing tag at index', {
      indexToRemove,
      tagToRemove: tags[indexToRemove],
      currentTagsLength: tags.length,
    });
    setTags(tags.filter((_, index) => index !== indexToRemove));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Log key event
    logDebug('Key pressed in TagInput', {
      key: e.key,
      inputValue,
      hasInputValue: !!inputValue,
      tagsLength: tags.length,
    });

    // Add tag on Enter
    if (e.key === 'Enter' && inputValue) {
      e.preventDefault();
      logDebug('Enter key pressed with input value, adding tag');
      addTag(inputValue);
      return;
    }

    // Remove last tag on Backspace when input is empty
    if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      e.preventDefault();
      logDebug('Backspace pressed on empty input, removing last tag');
      removeTag(tags.length - 1);
      return;
    }

    // Add tag on comma
    if (e.key === ',') {
      e.preventDefault();
      if (inputValue) {
        logDebug('Comma pressed, adding tag');
        addTag(inputValue);
        return;
      }
    }
  };

  return (
    <div
      className={cn(
        'flex flex-wrap gap-1 min-h-9 p-1 border rounded-md focus-within:ring-1 focus-within:ring-primary/20 focus-within:border-primary',
        disabled && 'bg-muted opacity-50 cursor-not-allowed',
        className
      )}
    >
      {tags.map((tag, index) => (
        <Badge
          key={`${tag}-${index}`}
          variant="secondary"
          className="flex h-6 items-center gap-1 px-2 text-xs"
        >
          {tag}
          {!disabled && (
            <button
              type="button"
              onClick={() => removeTag(index)}
              className="text-muted-foreground hover:text-foreground"
              aria-label={`Remove tag ${tag}`}
            >
              <X className="size-3" />
            </button>
          )}
        </Badge>
      ))}

      {tags.length < maxTags && !disabled && (
        <div className="flex min-w-[120px] flex-1 items-center">
          <Input
            id={id}
            value={inputValue}
            onChange={e => {
              const newValue = e.target.value;
              logDebug('Input changed in TagInput', { newValue });
              setInputValue(newValue);
            }}
            onKeyDown={handleKeyDown}
            placeholder=""
            className="h-6 border-0 p-0 text-xs shadow-none focus-visible:ring-0"
          />
          {inputValue && (
            <button
              type="button"
              onClick={() => {
                logDebug('Add tag button clicked', { inputValue });
                addTag(inputValue);
              }}
              className="p-1 text-muted-foreground hover:text-foreground"
              aria-label="Add tag"
            >
              <Plus className="size-3" />
            </button>
          )}
        </div>
      )}

      {tags.length >= maxTags && !disabled && (
        <Badge variant="outline" className="h-6 px-2 py-0 text-xs">
          Max {maxTags} tags
        </Badge>
      )}
    </div>
  );
}
